/** Fallback quando `VITE_SUPPORT_EMAIL` não está definido. */
export const SUPPORT_EMAIL_FALLBACK = 'support@linkpark.online';

export function getSupportEmail(): string {
  const fromEnv = import.meta.env.VITE_SUPPORT_EMAIL?.trim();
  return fromEnv || SUPPORT_EMAIL_FALLBACK;
}

export const SUPPORT_REPORT_CATEGORIES = [
  { id: 'bug', label: 'Bug no app' },
  { id: 'account', label: 'Conta/login' },
  { id: 'spots', label: 'Vagas ou reservas' },
  { id: 'notifications', label: 'Notificações' },
  { id: 'assistant', label: 'Assistente/IA' },
  { id: 'other', label: 'Outro' },
] as const;

export type SupportReportCategoryId = (typeof SUPPORT_REPORT_CATEGORIES)[number]['id'];

export function getSupportCategoryLabel(categoryId: SupportReportCategoryId): string {
  const found = SUPPORT_REPORT_CATEGORIES.find((item) => item.id === categoryId);
  return found?.label ?? 'Outro';
}

export function isSupportReportCategoryId(value: string): value is SupportReportCategoryId {
  return SUPPORT_REPORT_CATEGORIES.some((item) => item.id === value);
}

export interface SupportMailtoOptions {
  subject?: string;
  /** Linhas extras no corpo (ex.: uid, e-mail do usuário). */
  contextLines?: string[];
}

/** Monta um `mailto:` com assunto/corpo úteis para o suporte. */
export function buildSupportMailto({
  subject = 'Link Park — ajuda',
  contextLines = [],
}: SupportMailtoOptions = {}): string {
  const lines = [
    'Olá, preciso de ajuda com o Link Park.',
    '',
    'Descreva o problema aqui:',
    '',
    '---',
    ...contextLines,
    `User-Agent: ${typeof navigator !== 'undefined' ? navigator.userAgent : 'n/a'}`,
    `Página: ${typeof window !== 'undefined' ? window.location.href : 'n/a'}`,
  ];

  return buildMailtoHref({ subject, body: lines.join('\n') });
}

export interface SupportReportMailtoInput {
  categoryId: SupportReportCategoryId;
  description: string;
  userUid?: string | null;
  userEmail?: string | null;
  attachmentUrl?: string | null;
  /** Override para testes; em runtime usa `navigator.userAgent`. */
  userAgent?: string;
  /** Override para testes; em runtime usa `window.location.href`. */
  pageUrl?: string;
}

export function buildSupportReportSubject(categoryId: SupportReportCategoryId): string {
  return `Link Park — ${getSupportCategoryLabel(categoryId)}`;
}

export function buildSupportReportBody({
  categoryId,
  description,
  userUid,
  userEmail,
  attachmentUrl,
  userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : 'n/a',
  pageUrl = typeof window !== 'undefined' ? window.location.href : 'n/a',
}: SupportReportMailtoInput): string {
  const lines = [
    'Olá, preciso de ajuda com o Link Park.',
    '',
    `Categoria: ${getSupportCategoryLabel(categoryId)}`,
    '',
    'Descrição:',
    description.trim(),
    '',
    '---',
  ];

  if (userUid) lines.push(`UID: ${userUid}`);
  if (userEmail) lines.push(`E-mail: ${userEmail}`);
  if (attachmentUrl) lines.push(`Anexo: ${attachmentUrl}`);
  lines.push(`User-Agent: ${userAgent}`);
  lines.push(`Página: ${pageUrl}`);

  return lines.join('\n');
}

export function buildSupportReportMailto(input: SupportReportMailtoInput): string {
  return buildMailtoHref({
    subject: buildSupportReportSubject(input.categoryId),
    body: buildSupportReportBody(input),
  });
}

function buildMailtoHref({ subject, body }: { subject: string; body: string }): string {
  const params = new URLSearchParams({ subject, body });
  // URLSearchParams usa + para espaços; mailto espera %20.
  return `mailto:${getSupportEmail()}?${params.toString().replace(/\+/g, '%20')}`;
}
